import { defaultTo, map, orderBy } from "lodash";
import { IModel, ModelRecord, ISalesItemOptionModel, ISalesItemPriceModel, Model, SalesItemOptionModel, SalesItemPriceModel, SalesRequirementModel, SalesItemAttributeModel, SalesItemResetCategoryModel, SalesItemPriceRuleModel, ReportingGroupModel, MarketModel, AutoAddToVisitTypeModel, PhotoModel, SpeciesModel, BusinessLineModel, CommunicationTypeModel, SalesSubCategoryModel, SalesItemTypeModel, DiscountTypeModel, SalesItemDiscountSalesItemModel, SalesItemDiscountSubcategoryModel, SalesItemRebateSalesItemModel } from ".";
import { ISalesItemTraitModel, SalesItemTraitModel } from "./SalesItemTraitModel";

export interface ISalesItemModel extends IModel {
    salesItemId: number;
    salesCategoryId: number; //UI Field Only
    salesSubCategoryId: number;
    salesItemTypeId: number;
    discountTypeId: number;
    speciesId: number | undefined;
    name: string;
    fullName: string;
    description: string;
    helpPhotoId: number | undefined;
    isAutomaticallyGiven: boolean;
    isArchived: boolean;
    isRabiesVaccination: boolean;
    communicationTypeId: number | undefined;
    isPricedByOption: boolean;
    supplierProductCode: string;
    supplierProductName: string;
    salesItemUpgradeId: number | undefined;
    deepLinkedSalesSubCategoryId: number | undefined;
    autoAddToVisitTypeId: number;
    businessLineId: number;
    includeInDrugLog: boolean;
    businessLine: BusinessLineModel;
    communicationType: CommunicationTypeModel;
    salesSubCategory: SalesSubCategoryModel;
    deepLinkedSalesCategoryId: number | undefined; //UI Field Only
    deepLinkedSalesSubCategory: SalesSubCategoryModel;
    salesItemType: SalesItemTypeModel;
    discountType: DiscountTypeModel;
    species: SpeciesModel;
    helpPhoto: PhotoModel;
    autoAddToVisitType: AutoAddToVisitTypeModel;
    markets: MarketModel[];
    reportingGroups: ReportingGroupModel[];
    prices: ISalesItemPriceModel[];
    priceRules: SalesItemPriceRuleModel[];
    salesItemAttributes: SalesItemAttributeModel[];
    options: ISalesItemOptionModel[];
    resetCategories: SalesItemResetCategoryModel[];
    requirements: SalesRequirementModel[];
    satisfiers: SalesRequirementModel[];
    traits: ISalesItemTraitModel[];
    salesItemDiscountSalesItems: SalesItemDiscountSalesItemModel[];
    salesItemDiscountSubcategories: SalesItemDiscountSubcategoryModel[];
    salesItemRebateSalesItems: SalesItemRebateSalesItemModel[];
    defaultStartDate: string;//UI Field Only
    hospitalSKU: string;
    petcoSKU: string;
}

export class SalesItemModel extends Model implements ModelRecord<ISalesItemModel>
{
    salesItemId: number;
    salesCategoryId: number; //UI Field Only
    salesSubCategoryId: number;
    salesItemTypeId: number;
    discountTypeId: number;
    speciesId: number | undefined;
    name: string;
    fullName: string;
    description: string;
    helpPhotoId: number | undefined;
    isAutomaticallyGiven: boolean;
    isArchived: boolean;
    isRabiesVaccination: boolean;
    communicationTypeId: number | undefined;
    isPricedByOption: boolean;
    supplierProductCode: string;
    supplierProductName: string;
    salesItemUpgradeId: number | undefined;
    deepLinkedSalesCategoryId: number | undefined; //UI Field Only
    deepLinkedSalesSubCategoryId: number | undefined;
    autoAddToVisitTypeId: number;
    businessLineId: number;
    includeInDrugLog: boolean;
    businessLine: BusinessLineModel;
    communicationType: CommunicationTypeModel;
    salesSubCategory: SalesSubCategoryModel;
    deepLinkedSalesSubCategory: SalesSubCategoryModel;
    salesItemType: SalesItemTypeModel;
    discountType: DiscountTypeModel;
    species: SpeciesModel;
    helpPhoto: PhotoModel;
    autoAddToVisitType: AutoAddToVisitTypeModel;
    markets: MarketModel[];
    reportingGroups: ReportingGroupModel[];
    prices: SalesItemPriceModel[];
    priceRules: SalesItemPriceRuleModel[];
    salesItemAttributes: SalesItemAttributeModel[] = [];
    options: SalesItemOptionModel[] = [];
    resetCategories: SalesItemResetCategoryModel[];
    requirements: SalesRequirementModel[];
    satisfiers: SalesRequirementModel[];
    traits: SalesItemTraitModel[];
    salesItemDiscountSalesItems: SalesItemDiscountSalesItemModel[];
    salesItemDiscountSubcategories: SalesItemDiscountSubcategoryModel[];
    salesItemRebateSalesItems: SalesItemRebateSalesItemModel[];
    defaultStartDate: string;//UI Field Only
    hospitalSKU: string;
    petcoSKU: string;

    constructor(params?: Partial<ISalesItemModel>) {
        super(params);
        this.salesItemId = defaultTo(params?.salesItemId, 0);
        this.salesCategoryId = params?.salesSubCategory ? defaultTo(params?.salesSubCategory?.salesCategoryId, 0) : 0;
        this.salesSubCategoryId = defaultTo(params?.salesSubCategoryId, 0);
        this.salesItemTypeId = defaultTo(params?.salesItemTypeId, 0);
        this.discountTypeId = defaultTo(params?.discountTypeId, 0);
        this.speciesId = params?.speciesId;
        this.name = defaultTo(params?.name, '');
        this.fullName = defaultTo(params?.fullName, '');
        this.description = defaultTo(params?.description, '');
        this.helpPhotoId = defaultTo(params?.helpPhotoId, undefined);
        this.isAutomaticallyGiven = defaultTo(params?.isAutomaticallyGiven, false);
        this.isArchived = defaultTo(params?.isArchived, false);
        this.isRabiesVaccination = defaultTo(params?.isRabiesVaccination, false);
        this.communicationTypeId = defaultTo(params?.communicationTypeId, undefined);
        this.isPricedByOption = defaultTo(params?.isPricedByOption, false);
        this.supplierProductCode = defaultTo(params?.supplierProductCode, '');
        this.supplierProductName = defaultTo(params?.supplierProductName, '');
        this.salesItemUpgradeId = defaultTo(params?.salesItemUpgradeId, undefined);
        this.deepLinkedSalesCategoryId = params?.deepLinkedSalesSubCategory ? defaultTo(params?.deepLinkedSalesSubCategory?.salesCategoryId, undefined) : undefined;
        this.deepLinkedSalesSubCategoryId = params?.deepLinkedSalesSubCategoryId;
        this.autoAddToVisitTypeId = defaultTo(params?.autoAddToVisitTypeId, 0);
        this.businessLineId = defaultTo(params?.businessLineId, 0);
        this.includeInDrugLog = defaultTo(params?.includeInDrugLog, false);
        this.businessLine = params?.businessLine ? new BusinessLineModel(params.businessLine) : new BusinessLineModel();
        this.communicationType = params?.communicationType ? new CommunicationTypeModel(params.communicationType) : new CommunicationTypeModel();
        this.salesSubCategory = params?.salesSubCategory ? new SalesSubCategoryModel(params.salesSubCategory) : new SalesSubCategoryModel();
        this.deepLinkedSalesSubCategory = params?.deepLinkedSalesSubCategory ? new SalesSubCategoryModel(params.deepLinkedSalesSubCategory) : new SalesSubCategoryModel();
        this.salesItemType = params?.salesItemType ? new SalesItemTypeModel(params.salesItemType) : new SalesItemTypeModel();
        this.discountType = params?.discountType ? new DiscountTypeModel(params.discountType) : new DiscountTypeModel();
        this.species = params?.species ? new SpeciesModel(params.species) : new SpeciesModel();
        this.helpPhoto = params?.helpPhoto ? new PhotoModel(params.helpPhoto) : new PhotoModel();
        this.autoAddToVisitType = params?.autoAddToVisitType ? new AutoAddToVisitTypeModel(params.autoAddToVisitType) : new AutoAddToVisitTypeModel();
        this.markets = (params?.markets ?? []).map(p => new MarketModel(p));
        this.reportingGroups = (params?.reportingGroups ?? []).map(p => new ReportingGroupModel(p));
        this.prices = (params?.prices ?? []).map(p => new SalesItemPriceModel(p));
        this.priceRules = (params?.priceRules ?? []).map(p => new SalesItemPriceRuleModel(p));
        map(orderBy(params?.salesItemAttributes, v => v.sortOrder), (attribute) => { this.salesItemAttributes.push(new SalesItemAttributeModel(attribute)) });
        map(orderBy(params?.options, v => v.sortOrder), (sio) => { this.options.push(new SalesItemOptionModel(sio)) });
        this.resetCategories = (params?.resetCategories ?? []).map(p => new SalesItemResetCategoryModel(p));
        this.requirements = (params?.requirements ?? []).map(p => new SalesRequirementModel(p));
        this.satisfiers = (params?.satisfiers ?? []).map(p => new SalesRequirementModel(p));
        this.traits = (params?.traits ?? []).map(t => new SalesItemTraitModel(t));
        this.salesItemDiscountSalesItems = (params?.salesItemDiscountSalesItems ?? []).map(sids => new SalesItemDiscountSalesItemModel(sids));
        this.salesItemDiscountSubcategories = (params?.salesItemDiscountSubcategories ?? []).map(sids => new SalesItemDiscountSubcategoryModel(sids));
        this.salesItemRebateSalesItems = (params?.salesItemRebateSalesItems ?? []).map(sids => new SalesItemRebateSalesItemModel(sids));
        this.defaultStartDate = defaultTo(params?.defaultStartDate, (new Date()).toISOString());
        this.hospitalSKU = defaultTo(params?.hospitalSKU, "");
        this.petcoSKU = defaultTo(params?.petcoSKU, "");
    }
}
